import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import {
  ExternalLink,
  Eye,
  FileText,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  Trash2,
  UploadCloud,
} from 'lucide-react';

import { cn } from 'shared/lib/utils';
import { uploadFile } from 'shared/lib/uploadService';
import ConfirmModal from 'shared/components/ui/ConfirmModal/ConfirmModal';
import AIDocumentScanLoader, {
  type AIProcessingPhase,
} from 'shared/components/AIDocumentScanLoader';

const MAX_BYTES = 10 * 1024 * 1024;

const DEFAULT_ACCEPT: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};

export interface UploadedFileMeta {
  name: string;
  type: string;
  size: number;
  url: string;
}

export interface FileUploadProps {
  label: string;
  value?: UploadedFileMeta;
  onChange: (file: UploadedFileMeta | null) => void;
  onUploaded?: (file: UploadedFileMeta) => void;
  required?: boolean;
  error?: string;
  hint?: React.ReactNode;
  busy?: boolean;
  busyLabel?: string;
  /** Override accepted MIME types (passed through to react-dropzone). */
  accept?: Record<string, string[]>;
  /** Overrides the translated default formats copy when a slot is more restrictive. */
  acceptedFormatsLabel?: string;
  /**
   * When provided, the trash icon triggers this parent-controlled flow instead
   * of the built-in ConfirmModal. The parent is responsible for calling
   * `onChange(null)` after confirmation.
   */
  onRequestRemove?: () => void;
  /** Notifies the parent while the file is being uploaded to storage. */
  onUploadingChange?: (uploading: boolean) => void;
  /** When true, suppress the built-in spinner (replaced by AI scan loader). */
  hideInlineLoader?: boolean;
  /** Inline AI scan phase — renders a compact loader inside the dropzone. */
  processingPhase?: AIProcessingPhase | null;
  onProcessingDone?: () => void;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  value,
  onChange,
  onUploaded,
  required,
  error,
  hint,
  busy,
  busyLabel,
  accept,
  acceptedFormatsLabel,
  onRequestRemove,
  onUploadingChange,
  hideInlineLoader,
  processingPhase,
  onProcessingDone,
}) => {
  const { t } = useTranslation();
  const [localError, setLocalError] = useState<string | undefined>();
  const [isUploading, setIsUploading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [localPreview, setLocalPreview] = useState<{
    url: string;
    mime: string;
  } | null>(null);

  useEffect(() => {
    return () => {
      if (localPreview?.url.startsWith('blob:')) {
        URL.revokeObjectURL(localPreview.url);
      }
    };
  }, [localPreview]);

  const onDrop = useCallback(
    async (accepted: File[], rejected: FileRejection[]) => {
      setLocalError(undefined);

      if (rejected.length > 0) {
        const code = rejected[0].errors[0]?.code;
        if (code === 'file-too-large') {
          setLocalError(t('documents.fileTooLarge') as string);
        } else {
          setLocalError(t('documents.invalidType') as string);
        }
        return;
      }

      const file = accepted[0];
      if (!file) return;

      setLocalPreview((prev) => {
        if (prev?.url.startsWith('blob:')) URL.revokeObjectURL(prev.url);
        return { url: URL.createObjectURL(file), mime: file.type };
      });

      setIsUploading(true);
      onUploadingChange?.(true);
      try {
        const { url } = await uploadFile(file);
        const meta: UploadedFileMeta = {
          name: file.name,
          type: file.type || 'application/octet-stream',
          size: file.size,
          url,
        };
        onUploadingChange?.(false);
        onChange(meta);
        onUploaded?.(meta);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : (t('documents.invalidType') as string);
        setLocalError(message);
        onUploadingChange?.(false);
      } finally {
        setIsUploading(false);
      }
    },
    [onChange, onUploaded, onUploadingChange, t],
  );

  const isProcessing = processingPhase != null;
  const previewUrl = value?.url ?? localPreview?.url;
  const previewMime = value?.type ?? localPreview?.mime;

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: accept ?? DEFAULT_ACCEPT,
    multiple: false,
    maxSize: MAX_BYTES,
    onDrop,
    noClick: !!value,
    noKeyboard: !!value,
  });

  const errorMessage = localError || error;
  const isImage = value?.type.startsWith('image/');
  const isPdf = value?.type === 'application/pdf';

  return (
    <div className="flex flex-col gap-2 w-full">
      {(label || value) && (
        <div className="flex items-center justify-between gap-2">
          {label ? (
            <span className="text-sm font-semibold text-gray-900">
              {label}
              {required && <span className="ms-1 text-[#bf0035]">*</span>}
            </span>
          ) : (
            <span />
          )}
          {value && (
            <span className="text-xs text-emerald-600 font-medium truncate max-w-[60%]">
              {value.name}
            </span>
          )}
        </div>
      )}

      <div
        {...getRootProps()}
        className={cn(
          'relative flex min-h-[120px] w-full flex-col items-center justify-center rounded-xl border border-dashed bg-white p-4 transition-colors',
          isDragActive
            ? 'border-[#bf0035] bg-[#bf0035]/[0.04]'
            : 'border-gray-300 hover:border-[#bf0035]/40 hover:bg-[#bf0035]/[0.02]',
          !value && !isProcessing && 'cursor-pointer',
          errorMessage && 'border-red-400',
          isProcessing &&
            'cursor-default border-[#bf0035]/20 bg-[#bf0035]/[0.03]',
          value &&
            !isProcessing &&
            'cursor-default border-solid border-emerald-300 bg-emerald-50/40',
        )}
      >
        <input {...getInputProps()} />

        {isProcessing && processingPhase && (
          <AIDocumentScanLoader
            phase={processingPhase}
            previewUrl={previewUrl}
            previewMime={previewMime}
            onDone={() => {
              setLocalPreview((prev) => {
                if (prev?.url.startsWith('blob:')) URL.revokeObjectURL(prev.url);
                return null;
              });
              onProcessingDone?.();
            }}
          />
        )}

        {!isProcessing && !value && !isUploading && !busy && (
          <div className="flex flex-col items-center text-center gap-1.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#bf0035]/[0.07] text-[#bf0035]">
              <UploadCloud size={20} />
            </div>
            <p className="text-sm font-medium text-gray-700">
              {t('documents.dropOrClick')}
            </p>
            <p className="text-sm text-gray-500">
              {acceptedFormatsLabel ?? t('documents.acceptedFormats')}
            </p>
          </div>
        )}

        {(isUploading || busy) && !hideInlineLoader && (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="animate-spin text-haat-red-600" size={28} />
            <span className="text-sm text-gray-600">
              {busyLabel || (t('common.uploading') as string)}
            </span>
          </div>
        )}

        {!isProcessing && value && !isUploading && !busy && (
          <div className="flex w-full items-center gap-3">
            <a
              href={value.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex-shrink-0 group"
              title={t('documents.preview') as string}
            >
              {isImage ? (
                <img
                  src={value.url}
                  alt={value.name}
                  className="w-16 h-16 rounded-xl object-cover border border-gray-200 group-hover:border-haat-red-300 transition-colors"
                />
              ) : isPdf ? (
                <div className="w-16 h-16 rounded-xl bg-haat-red-50 text-haat-red-600 flex items-center justify-center group-hover:bg-haat-red-100 transition-colors">
                  <FileText size={26} />
                </div>
              ) : (
                <div className="w-16 h-16 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                  <ImageIcon size={26} />
                </div>
              )}
            </a>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">
                {value.name}
              </p>
              <p className="text-xs text-gray-500">{formatBytes(value.size)}</p>
            </div>
            <div className="flex items-center gap-1">
              <a
                href={value.url}
                target="_blank"
                rel="noreferrer"
                className="p-2 rounded-lg text-gray-500 hover:text-haat-red-600 hover:bg-white transition-colors"
                title={t('documents.preview') as string}
                onClick={(e) => e.stopPropagation()}
              >
                {isImage ? <Eye size={16} /> : <ExternalLink size={16} />}
              </a>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  open();
                }}
                className="p-2 rounded-lg text-gray-500 hover:text-haat-red-600 hover:bg-white transition-colors"
                title={t('common.replace') as string}
              >
                <RefreshCw size={16} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onRequestRemove) {
                    onRequestRemove();
                  } else {
                    setConfirmDeleteOpen(true);
                  }
                }}
                className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-white transition-colors"
                title={t('common.remove') as string}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {errorMessage ? (
        <span className="text-sm font-medium text-red-600">{errorMessage}</span>
      ) : hint ? (
        typeof hint === 'string' ? (
          <span className="text-sm text-gray-500">{hint}</span>
        ) : (
          <div className="text-sm text-gray-500">{hint}</div>
        )
      ) : null}

      <ConfirmModal
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={() => {
          onChange(null);
          setConfirmDeleteOpen(false);
        }}
        title={t('common.delete.title') as string}
        message={t('common.delete.message') as string}
        description={value?.name}
        confirmText={t('common.delete.confirm') as string}
        cancelText={t('common.cancel') as string}
        variant="danger"
      />
    </div>
  );
};

export default FileUpload;
